import Card from '../../components/Card';
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useDatabase } from '../../context/DatabaseContext';
import { motion } from 'framer-motion';
import HeroBackground from '../../components/ui/HeroBackground';
import ajrakTexture from '../../assets/ajrak_texture_new.jpg';
import { useT } from '../../utils/useTranslation';

export default function BookDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const t = useT();
  const { getDocument } = useDatabase();
  const [book, setBook] = useState(null);
  const [author, setAuthor] = useState(null);
  const [loading, setLoading] = useState(true);
  const isRTL = t.language === 'sindhi';

  useEffect(() => {
    const fetchBook = async () => {
      try {
        // Try published_books first (admin-created), then books (legacy)
        let bookData = await getDocument('published_books', id);
        if (!bookData) {
          bookData = await getDocument('books', id);
        }
        if (bookData) {
          setBook(bookData);
          // If there's a linked author member, fetch their name
          if (bookData.author_id) {
            try {
              const memberData = await getDocument('members', bookData.author_id);
              if (memberData) setAuthor(memberData);
            } catch (e) {
              console.error("Could not fetch author member:", e);
            }
          }
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchBook();
  }, [id]);

  const typeIcon = t => ({ book: '📗', journal: '📔', magazine: '📰' })[t] || '📄';

  if (loading) return (
    <div className="hero-premium" style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <HeroBackground />
      <div style={{ position: 'relative', zIndex: 10 }}><div className="spinner" /></div>
    </div>
  );

  if (!book) return (
    <>
      <div className="hero-premium">
        <HeroBackground />
        <div style={{ padding: '100px 2rem', color: 'white', textAlign: 'center', position: 'relative', zIndex: 10 }}>
          <h1 style={{ fontFamily: isRTL ? 'var(--font-sindhi)' : 'var(--font-display)', fontSize: 40, fontWeight: 800 }}>
            {isRTL ? 'ڪتاب نه مليو' : 'Book Not Found'}
          </h1>
        </div>
      </div>
      <div style={{ background: 'var(--sindhi-ivory)', backgroundImage: `url(${ajrakTexture})`, backgroundSize: '600px 600px', backgroundRepeat: 'repeat', position: 'relative', zIndex: 10, marginTop: '-40px' }}>
        <div style={{ maxWidth: 600, margin: '0 auto', textAlign: 'center', padding: '60px 24px 100px', position: 'relative', zIndex: 1 }}>
          <p style={{ color: 'var(--sindhi-text-muted)', fontSize: 18, marginBottom: 32, fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>
            {isRTL ? 'اهو ڪتاب جيڪو توهان ڳولي رهيا آهيو موجود ناهي يا ختم ڪيو وئي آهي.' : "The book you're looking for doesn't exist or has been removed."}
          </p>
          <button className="btn btn-primary" onClick={() => navigate('/books')}>{t('backToLibrary') || (isRTL ? '← لائبريري ڏانهن' : '← Back to Library')}</button>
        </div>
      </div>
    </>
  );

  // Support both new bilingual fields (title_en/title_sd) and legacy fields (title/titleSindhi)
  const bookTitle = isRTL
    ? (book.title_sd || book.titleSindhi || book.title_en || book.title || '')
    : (book.title_en || book.title || book.title_sd || '');
  const bookTitleAlt = isRTL
    ? (book.title_en || book.title || '')
    : (book.title_sd || book.titleSindhi || '');
  const bookAuthor = isRTL
    ? (book.author_sd || book.authorSindhi || book.author_en || book.author || '')
    : (book.author_en || book.author || book.author_sd || '');
  const bookAuthorAlt = isRTL
    ? (book.author_en || book.author || '')
    : (book.author_sd || book.authorSindhi || '');
  const bookPublisher = isRTL
    ? (book.publisher_sd || book.publisherSindhi || book.publisher_en || book.publisher || '')
    : (book.publisher_en || book.publisher || book.publisher_sd || '');
  const bookDesc = isRTL
    ? (book.description_sd || book.descriptionSindhi || book.description_en || book.description || '')
    : (book.description_en || book.description || book.description_sd || '');

  // If author member is linked, prefer their name
  const displayAuthor = author
    ? (isRTL ? (author.full_name_sindhi || author.full_name) : author.full_name)
    : bookAuthor;

  const isPDF = book.file && book.file.endsWith('.pdf');
  const year = book.publication_year || book.year;

  return (
    <>
      <div className="hero-premium">
        <HeroBackground />
        <div style={{ maxWidth: 1000, margin: '0 auto', position: 'relative', zIndex: 10, padding: '0 2rem', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
          <Link to="/books" style={{ color: 'var(--sindhi-gold)', textDecoration: 'none', fontSize: 14, fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 8, marginBottom: 24, fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>
            {isRTL ? '← لائبريري ڏانهن واپس' : `← ${t('backToLibrary') || 'Back to Library'}`}
          </Link>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} style={{ fontFamily: isRTL ? 'var(--font-sindhi)' : 'var(--font-display)', fontSize: isRTL ? 56 : 48, fontWeight: 800, marginBottom: 8, letterSpacing: -1 }}>
            {bookTitle}
          </motion.h1>
          {bookTitleAlt && (
            <p style={{ fontSize: 24, fontFamily: isRTL ? 'inherit' : 'var(--font-sindhi)', color: 'var(--sindhi-gold)', marginBottom: 20, direction: isRTL ? 'ltr' : 'rtl' }}>
              {bookTitleAlt}
            </p>
          )}
        </div>
      </div>

      <div style={{ background: 'var(--sindhi-ivory)', backgroundImage: `url(${ajrakTexture})`, backgroundSize: '600px 600px', backgroundRepeat: 'repeat', position: 'relative', zIndex: 10, marginTop: '-60px' }}>
        <div style={{ position: 'absolute', inset: 0, backdropFilter: 'blur(3px)', WebkitBackdropFilter: 'blur(3px)', background: 'rgba(253,248,235,0.7)', zIndex: 0, pointerEvents: 'none' }} />
        <div style={{ maxWidth: 1000, margin: '0 auto', padding: '0 24px 100px', position: 'relative', zIndex: 1 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '320px 1fr', gap: 48, direction: isRTL ? 'rtl' : 'ltr' }}>
            {/* Cover/Image */}
            <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}>
              <div style={{ borderRadius: 24, overflow: 'hidden', background: 'var(--sindhi-navy)', display: 'flex', alignItems: 'center', justifyContent: 'center', height: 440, marginBottom: 24, boxShadow: '0 30px 60px -12px rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.1)' }}>
                {(book.cover_image_url || book.cover || book.image) ? (
                  <img src={book.cover_image_url || book.cover || book.image} alt={bookTitle} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  <div style={{ textAlign: 'center', opacity: 0.3 }}>
                    <div style={{ fontSize: 80, marginBottom: 12 }}>{typeIcon(book.type || book.genre)}</div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: 'white' }}>{isRTL ? 'ڪو تصوير ناهي' : 'No Cover'}</div>
                  </div>
                )}
              </div>
              {book.file && (
                <motion.a whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} href={book.file} target="_blank" rel="noopener noreferrer" className="btn btn-primary" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', textDecoration: 'none', height: 60, borderRadius: 16, fontSize: 16, fontWeight: 800, background: 'var(--sindhi-maroon)', border: 'none', boxShadow: '0 12px 24px -6px var(--sindhi-maroon-glow)', fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>
                  {isPDF ? `📄 ${t('viewMore') || 'View PDF'}` : `⬇️ ${t('downloadsLabel') || 'Download'}`}
                </motion.a>
              )}
            </motion.div>

            {/* Details */}
            <motion.div initial={{ opacity: 0, x: isRTL ? -20 : 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.1 }}>
              <Card style={{ padding: 48, background: 'white', textAlign: isRTL ? 'right' : 'left' }}>
                {/* Type & Genre Badges */}
                <div style={{ display: 'flex', gap: 12, marginBottom: 24, flexWrap: 'wrap' }}>
                  {(book.type || book.genre) && (
                    <span style={{ fontSize: 11, fontWeight: 800, textTransform: 'uppercase', background: 'var(--sindhi-navy)', color: 'white', padding: '6px 14px', borderRadius: 8, letterSpacing: 1 }}>
                      {book.genre || book.type}
                    </span>
                  )}
                  {book.language && (
                    <span style={{ fontSize: 11, fontWeight: 800, textTransform: 'uppercase', background: 'var(--sindhi-gold-muted)', color: 'var(--sindhi-navy)', padding: '6px 14px', borderRadius: 8, letterSpacing: 1 }}>
                      {book.language}
                    </span>
                  )}
                </div>

                {/* Author & Year */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 32, marginBottom: 40 }}>
                  {displayAuthor && (
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 800, color: 'var(--sindhi-text-muted)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6, fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>{t('author')}</div>
                      <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--sindhi-navy)', fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>{displayAuthor}</div>
                      {bookAuthorAlt && <div style={{ fontSize: 13, color: 'var(--sindhi-text-muted)', fontFamily: isRTL ? 'inherit' : 'var(--font-sindhi)', direction: isRTL ? 'ltr' : 'rtl', marginTop: 4 }}>{bookAuthorAlt}</div>}
                    </div>
                  )}
                  {year && (
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 800, color: 'var(--sindhi-text-muted)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6, fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>{t('publishedYear')}</div>
                      <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--sindhi-navy)' }}>{year}</div>
                    </div>
                  )}
                  {bookPublisher && (
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 800, color: 'var(--sindhi-text-muted)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6, fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>{t('publisher')}</div>
                      <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--sindhi-navy)', fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>{bookPublisher}</div>
                    </div>
                  )}
                  {book.isbn && (
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 800, color: 'var(--sindhi-text-muted)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>ISBN</div>
                      <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--sindhi-navy)' }}>{book.isbn}</div>
                    </div>
                  )}
                  {book.category && (
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 800, color: 'var(--sindhi-text-muted)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6, fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>{t('categoryLabel')}</div>
                      <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--sindhi-navy)', fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>{book.category}</div>
                    </div>
                  )}
                </div>

                {/* Divider */}
                <div style={{ height: 1, background: 'var(--sindhi-cream)', marginBottom: 32 }} />

                {/* Description */}
                {bookDesc && (
                  <div>
                    <h2 style={{ fontSize: 13, fontWeight: 800, color: 'var(--sindhi-text-muted)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 16, fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>
                      {t('description')}
                    </h2>
                    <p style={{ fontSize: 16, lineHeight: 1.9, color: 'var(--sindhi-text)', whiteSpace: 'pre-wrap', opacity: 0.9, fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>
                      {bookDesc}
                    </p>
                  </div>
                )}
              </Card>
            </motion.div>
          </div>
        </div>
      </div>
    </>
  );
}